/**
 * The panel beside the sign-up and login forms: the product doing its job.
 *
 * A conversation streams in as a live transcript, a note rises and writes itself, and
 * the doctor reviews it, adds a line of their own and copies it to their chart. The
 * voice ribbon behind swells while people speak. Everything is drawn in code, so it stays
 * sharp at any zoom and there is no photo to crop.
 *
 * Without JavaScript, with reduced motion, or when it has finished playing, the markup
 * rests on its final frame: the copied note. It never touches the form.
 */

import React, { useLayoutEffect, useRef, useState } from 'react'
import ChartiSymbol from '../icons/ChartiSymbol'
import { hasSignedInHere } from '../../utils/auth-token'
import { createVoiceRibbon, type VoiceRibbon } from './voiceRibbon'
import styles from './AuthStory.module.css'

const TRUST_POINTS = ['Built by physicians', 'Hosted in Canada', 'No training on your data']

// A fictional visit. Doctors will read this closely: every line of the note must come
// from something said in the transcript, or it looks like the AI made up findings.
const TRANSCRIPT = [
  { who: 'Doctor', text: 'Any chest pain when you climb stairs?', at: 0.7 },
  { who: 'Patient', text: 'No pain, I just get short of breath.', at: 3 },
  { who: 'Doctor', text: 'How many flights before that starts?', at: 5.3 },
  { who: 'Patient', text: 'About two, then I need a minute.', at: 7.5 },
  {
    who: 'Doctor',
    text: "Your lungs sound clear, so I'd like to check your heart. We'll order an ECG and bloodwork today.",
    at: 9.6,
  },
]

const NOTE = [
  { letter: 'S', text: 'Shortness of breath after about two flights of stairs. No chest pain.', at: 14.2 },
  { letter: 'O', text: 'Lungs clear on auscultation.', at: 15.8 },
  { letter: 'A', text: 'Exertional dyspnea, rule out cardiac cause.', at: 16.7 },
  { letter: 'P', text: 'ECG and bloodwork ordered.', at: 17.8 },
]

/** The doctor's own words, which is the point: Charti drafts it, and it is their note. */
const DOCTOR_EDIT = 'Follow up in 2 weeks.'

/** The first play starts a line in, so the page never opens on an empty panel. */
const FIRST_PLAY_FROM = 2.4
const WRITE_AT = 13.4
const REVIEW_AT = 18.8
const EDIT_AT = 20.1
const CURSOR_TO_COPY_AT = 21.7
const COPIED_AT = 22.9
const OUT_AT = 26.4
const LOOP_SECONDS = 27
/** Then it rests on the copied note, with Play to see it again. */
const LOOPS = 3
/** Someone logging in again has seen the story: the note rests and only the ribbon moves, for this long. */
const REST_RIBBON_SECONDS = 30
const SEGMENTS = [
  { from: 0, to: WRITE_AT },
  { from: WRITE_AT, to: REVIEW_AT },
  { from: REVIEW_AT, to: OUT_AT },
]
const FRAME_MS = 30
/** How far the ribbon may stray past the gap kept for it, behind the text on each side. */
const RIBBON_OVERLAP_PX = 20

const phaseAt = (t: number) => (t < WRITE_AT ? 'listen' : t < REVIEW_AT ? 'write' : t < OUT_AT ? 'review' : 'out')

const cursorStageAt = (t: number) => (t < REVIEW_AT || t >= OUT_AT ? 0 : t < CURSOR_TO_COPY_AT ? 1 : 2)

/** How loud the ribbon should be: lively during speech, calm once Charti is writing. */
const energyAt = (t: number) => {
  for (const line of TRANSCRIPT) {
    const speaking = line.text.split(' ').length * 0.17 + 0.5
    if (t >= line.at && t < line.at + speaking) {
      return 0.55 + 0.45 * Math.abs(Math.sin(t * 7.3) * Math.sin(t * 2.9))
    }
  }
  if (t < WRITE_AT) return 0.14
  const rise = Math.exp(-Math.pow((t - WRITE_AT - 0.3) * 1.8, 2))
  const copied = Math.exp(-Math.pow((t - COPIED_AT - 0.2) * 2.2, 2))
  return Math.max(t < REVIEW_AT ? 0.26 : 0.14, 0.8 * rise, 0.5 * copied)
}

const Words: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(' ').map((word, index) => (
      <React.Fragment key={index}>
        <span className={styles.word} style={{ '--i': index } as React.CSSProperties}>
          {word}
        </span>{' '}
      </React.Fragment>
    ))}
  </>
)

export const TrustPoints: React.FC<{ className?: string }> = ({ className }) => (
  <ul className={className}>
    {TRUST_POINTS.map((point) => (
      <li key={point}>
        <svg className={styles.check} viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3.5 8.5 6.5 11.5 12.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {point}
      </li>
    ))}
  </ul>
)

type Mode = 'still' | 'playing' | 'paused'

export const AuthStory: React.FC = () => {
  const panel = useRef<HTMLElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const voice = useRef<HTMLDivElement>(null)
  const clock = useRef<HTMLSpanElement>(null)
  const sheet = useRef<HTMLDivElement>(null)
  const edit = useRef<HTMLSpanElement>(null)
  const copy = useRef<HTMLSpanElement>(null)
  const cursor = useRef<SVGSVGElement>(null)
  const toggle = useRef<() => void>(() => {})
  const [mode, setMode] = useState<Mode>('still')

  useLayoutEffect(() => {
    const node = panel.current
    const surface = canvas.current
    if (!node || !surface) return

    const steps = [...node.querySelectorAll<HTMLElement>('[data-at]')].map((element) => ({
      element,
      at: Number(element.dataset.at),
    }))
    const segments = [...node.querySelectorAll<HTMLElement>('[data-segment]')]
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')

    let ribbon: VoiceRibbon | null = null
    let ribbonTimer = 0
    let frame = 0
    let last = 0
    let elapsed = FIRST_PLAY_FROM
    let loops = 0
    let energy = 0.14
    let ribbonTime = 9
    let shown = false
    let onScreen = true
    let userPaused = false
    let finished = false
    let shownSecond = -1
    let cursorStage = -1
    let resting = window.location.pathname === '/login' && hasSignedInHere()
    let restedFor = 0

    const placeRibbon = () => {
      const anchor = voice.current?.getBoundingClientRect()
      const box = node.getBoundingClientRect()
      if (!ribbon || !anchor || !box.height) return
      // In the rail beside the form there is no gap to fit: it runs through the middle.
      if (!anchor.height) {
        ribbon.place(0.5, 0.2)
        return
      }
      ribbon.place(
        1 - (anchor.top + anchor.height / 2 - box.top) / box.height,
        (anchor.height / 2 + RIBBON_OVERLAP_PX) / box.height,
      )
    }

    // Compiling a shader can take a moment on an old laptop, and the form comes first:
    // start just after it has painted, then fade in over the CSS background.
    const startRibbon = () => {
      if (ribbonTimer) return
      ribbonTimer = window.setTimeout(() => {
        ribbon = createVoiceRibbon(surface)
        if (!ribbon) return
        placeRibbon()
        ribbon.prepare(ribbonTime, energy)
        node.toggleAttribute('data-ribbon', true)
      }, 60)
    }

    const moveCursor = (stage: number) => {
      const paper = sheet.current
      const pointer = cursor.current
      if (!paper || !pointer) return
      node.toggleAttribute('data-cursor', stage > 0)
      const box = paper.getBoundingClientRect()
      const target = stage === 1 ? edit.current?.querySelector('span') : stage === 2 ? copy.current : null
      if (!target) {
        pointer.style.transform = `translate(${box.width - 48}px, ${box.height + 24}px)`
        return
      }
      const rect = target.getBoundingClientRect()
      const x = stage === 1 ? rect.left : rect.left + rect.width * 0.62
      const y = rect.top + rect.height * (stage === 1 ? 0.7 : 0.55)
      pointer.style.transform = `translate(${x - box.left}px, ${y - box.top}px)`
    }

    const show = (t: number) => {
      const phase = phaseAt(t)
      if (node.dataset.phase !== phase) node.dataset.phase = phase
      for (const step of steps) step.element.toggleAttribute('data-on', t >= step.at)
      node.toggleAttribute('data-copied', t >= COPIED_AT && t < OUT_AT)
      const stage = cursorStageAt(t)
      if (stage !== cursorStage) {
        cursorStage = stage
        moveCursor(stage)
      }
      segments.forEach((segment, index) => {
        const { from, to } = SEGMENTS[index]
        segment.style.setProperty('--p', String(Math.min(1, Math.max(0, (t - from) / (to - from)))))
      })
      const second = Math.floor(Math.min(t, WRITE_AT))
      if (second !== shownSecond && clock.current) {
        shownSecond = second
        clock.current.textContent = `00:${String(second).padStart(2, '0')}`
      }
    }

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick)
      const delta = now - last
      if (delta < FRAME_MS) return
      last = now
      const seconds = Math.min(delta, 120) / 1000
      if (resting) {
        restedFor += seconds
        if (restedFor >= REST_RIBBON_SECONDS) {
          finished = true
          sync()
          return
        }
      } else {
        elapsed += seconds
        if (loops === LOOPS - 1 && elapsed >= OUT_AT - 0.1) {
          finished = true
          sync()
          return
        }
        if (elapsed >= LOOP_SECONDS) {
          elapsed -= LOOP_SECONDS
          loops += 1
        }
        show(elapsed)
      }
      energy += ((resting ? 0.14 : energyAt(elapsed)) - energy) * (1 - Math.exp(-seconds * 6))
      ribbonTime += seconds
      ribbon?.draw(ribbonTime, energy)
    }

    const halt = () => {
      if (frame) cancelAnimationFrame(frame)
      frame = 0
    }

    const sync = () => {
      if (!shown) {
        halt()
        return
      }
      startRibbon()
      if (reducedMotion?.matches) {
        halt()
        delete node.dataset.animated
        delete node.dataset.phase
        node.removeAttribute('data-cursor')
        node.removeAttribute('data-copied')
        node.removeAttribute('data-paused')
        cursorStage = -1
        for (const step of steps) step.element.removeAttribute('data-on')
        setMode('still')
        return
      }
      if (node.dataset.animated === undefined) {
        node.dataset.animated = ''
        show(resting ? OUT_AT - 0.1 : elapsed)
      }
      const playing = onScreen && !document.hidden && !userPaused && !finished
      node.toggleAttribute('data-paused', !playing)
      setMode(playing || !(userPaused || finished) ? 'playing' : 'paused')
      if (playing && !frame) {
        ribbon?.restartClock()
        last = performance.now()
        frame = requestAnimationFrame(tick)
      } else if (!playing) {
        halt()
      }
    }

    toggle.current = () => {
      if (finished) {
        finished = false
        userPaused = false
        resting = false
        elapsed = 0
        loops = 0
        restedFor = 0
        show(0)
      } else {
        userPaused = !userPaused
      }
      sync()
    }

    const measure = () => {
      shown = node.offsetWidth > 0
      if (shown && ribbon) {
        ribbon.resize()
        placeRibbon()
        ribbon.draw(ribbonTime, energy, true)
      }
      sync()
    }

    // Once they start typing, let this pass finish and then stay still beside the form.
    const onTyping = (event: Event) => {
      if (!node.contains(event.target as Node)) loops = Math.max(loops, LOOPS - 1)
    }

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    resizeObserver?.observe(node)
    const intersectionObserver =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => {
            onScreen = entry.isIntersecting
            sync()
          })
    intersectionObserver?.observe(node)
    document.addEventListener('visibilitychange', sync)
    document.addEventListener('input', onTyping)
    reducedMotion?.addEventListener?.('change', sync)
    // The headline's height changes once its font arrives, and the ribbon follows it.
    void document.fonts?.ready.then(placeRibbon)
    measure()

    return () => {
      halt()
      window.clearTimeout(ribbonTimer)
      resizeObserver?.disconnect()
      intersectionObserver?.disconnect()
      document.removeEventListener('visibilitychange', sync)
      document.removeEventListener('input', onTyping)
      reducedMotion?.removeEventListener?.('change', sync)
      ribbon?.destroy()
      delete node.dataset.animated
      node.removeAttribute('data-ribbon')
    }
  }, [])

  return (
    <aside ref={panel} className={styles.story} aria-label="How Charti works">
      <canvas ref={canvas} className={styles.canvas} aria-hidden="true" />
      <div className={styles.inner}>
        <p className={styles.headline}>
          Chart less.
          <em>See patients more.</em>
        </p>
        <div ref={voice} className={styles.voice} aria-hidden="true" />
        <div className={styles.stage} aria-hidden="true">
          <div className={styles.listening}>
            <div className={styles.listeningHead}>
              <span className={styles.avatar}>
                <ChartiSymbol viewBox="-8.53 -10.125 160 160" width="82%" height="82%" color="#155eef" />
              </span>
              <span>Charti is listening</span>
              <span className={styles.example}>Example visit</span>
              <span className={styles.recording}>
                <span className={styles.recordingDot} />
                <span ref={clock}>00:00</span>
              </span>
            </div>
            <ol className={styles.transcript}>
              {TRANSCRIPT.map((line) => (
                <li key={line.at} className={styles.line} data-at={line.at} data-who={line.who}>
                  <span className={styles.who}>{line.who}</span>
                  <span>
                    <Words text={line.text} />
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div ref={sheet} className={styles.sheet}>
            <div className={styles.sheetHead}>
              <span className={styles.sheetTitle}>Visit note</span>
              <span className={styles.sheetFormat}>Example · SOAP</span>
            </div>
            <div className={styles.rows}>
              {NOTE.map((row, index) => (
                <div key={row.letter} className={styles.row} data-at={row.at}>
                  <span className={styles.letter}>{row.letter}</span>
                  <span>
                    <Words text={row.text} />
                    {index === NOTE.length - 1 && (
                      <span ref={edit} className={styles.edit} data-at={EDIT_AT}>
                        <Words text={DOCTOR_EDIT} />
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className={styles.sheetFoot}>
              <span className={styles.status}>
                <span className={styles.statusWriting}>Charti is writing</span>
                <span className={styles.statusDraft}>Draft for your review</span>
                <span className={styles.statusCopied}>Paste it into your chart</span>
              </span>{' '}
              <span ref={copy} className={styles.copy}>
                <span className={styles.copyLabel}>Copy All</span>
                <span className={styles.copiedLabel}>
                  <svg viewBox="0 0 16 16" fill="none">
                    <path d="M3.5 8.5 6.5 11.5 12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Copied
                </span>
              </span>
            </div>
            <svg ref={cursor} className={styles.cursor} viewBox="0 0 20 22" fill="none">
              <path d="M3 2.5v15.2l4.1-3.8 2.7 6.1 3-1.3-2.7-6h5.6L3 2.5Z" fill="#fff" stroke="#0b1b3f" strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <div className={styles.caption}>
          <p className={styles.captionText}>
            <span className={styles.captionListen}>Press record and talk to your patient.</span>{' '}
            <span className={styles.captionWrite}>Charti writes the note.</span>{' '}
            <span className={styles.captionReview}>You review it and paste it in.</span>
          </p>
          <div className={styles.segments} aria-hidden="true">
            {SEGMENTS.map((segment) => (
              <span key={segment.from} className={styles.segment} data-segment />
            ))}
          </div>
        </div>

        <TrustPoints className={styles.checks} />
      </div>

      {mode !== 'still' && (
        <button
          type="button"
          className={styles.control}
          onClick={() => toggle.current()}
          aria-label={mode === 'paused' ? 'Play the animation' : 'Pause the animation'}>
          <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            {mode === 'paused' ? (
              <path d="M5 3.2v9.6c0 .5.5.8 1 .5l7.2-4.8c.4-.3.4-.8 0-1.1L6 2.7c-.5-.3-1 0-1 .5Z" />
            ) : (
              <path d="M4.5 3h2.2c.2 0 .3.1.3.3v9.4c0 .2-.1.3-.3.3H4.5c-.2 0-.3-.1-.3-.3V3.3c0-.2.1-.3.3-.3Zm4.8 0h2.2c.2 0 .3.1.3.3v9.4c0 .2-.1.3-.3.3H9.3c-.2 0-.3-.1-.3-.3V3.3c0-.2.1-.3.3-.3Z" />
            )}
          </svg>
        </button>
      )}
    </aside>
  )
}
